import { Injectable } from '@angular/core';

import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import {
  EmployeeLicensesData,
  IAccountPlanItemsResponse,
  IAccountPlanUI,
  IPlanContentUnpacked,
  IPlanWithPrevious,
  IPlanWithSubtitle,
  IUnnecessaryFields
} from '@shared/interfaces/account-plan.interface';

marker('Basis');
marker('Smart');
marker('Proff');

export enum PlansEnum {
  Free = 'Free',
  Lead = 'Lead',
  Trial = 'Trial',
  Basis = 'Start',
  Smart = 'Smart',
  Regular = 'Regular',
  Proff = 'Total',
  Total = 'Total',
  Pro = 'Pro'
}

@Injectable({
  providedIn: 'root'
})
export class PlanMapperService {
  subtitle = new Map()
    .set(PlansEnum.Basis, marker('For companies with basic needs'))
    .set(PlansEnum.Smart, marker('Perfect for growing businesses'))
    .set(PlansEnum.Proff, marker('Complete system for professionals'));

  mapPlans(plans: IAccountPlanUI[], licenses: EmployeeLicensesData): IAccountPlanUI[] {
    return plans.map((plan: IAccountPlanUI) => {
      const newPlan = this.removeUnnecessaryFields(plan);
      const planWithFeatures = this.unpackContent(newPlan);
      const planWithSubtitle = this.addSubtitle(planWithFeatures);
      const planWithPrevious = this.addPrevious(planWithSubtitle);
      return this.addLicensesQuantity(planWithPrevious, licenses);
    });
  }

  addLicensesQuantity(plan: IPlanWithPrevious, licenses: EmployeeLicensesData): IAccountPlanUI {
    return { ...plan, licensesQuantity: licenses.activeUsers };
  }

  unpackContent(plan: IUnnecessaryFields): IPlanContentUnpacked {
    const planFeatures = plan.content?.plan_include ? plan.content?.plan_include.map((feature) => feature.value) : [];
    const planDescription = plan.content?.description ? plan.content?.description.map((feature) => feature.value).toString() : '';
    delete plan.content;
    return { ...plan, features: planFeatures, description: planDescription };
  }

  addSubtitle(plan: IPlanContentUnpacked): IPlanWithSubtitle {
    const { display_name } = plan;
    const subtitle = this.subtitle.has(display_name) ? this.subtitle.get(display_name) : null;
    return { ...plan, subtitle };
  }

  addPrevious(plan: IPlanWithSubtitle): IPlanWithPrevious {
    const { display_name } = plan;

    switch (display_name) {
      case PlansEnum.Basis:
        return { ...plan, previousPlan: null, popular: false };
      case PlansEnum.Smart:
        return { ...plan, previousPlan: PlansEnum.Basis, popular: true };
      case PlansEnum.Proff:
        return { ...plan, previousPlan: PlansEnum.Smart, popular: true };
      case PlansEnum.Pro:
        return { ...plan, previousPlan: PlansEnum.Smart, popular: true };
    }
  }

  removeUnnecessaryFields(plan: IAccountPlanUI): IUnnecessaryFields {
    return {
      id: plan.id,
      name: plan.name,
      display_name: plan.display_name,
      content: plan.content,
      plan_prices: plan.plan_prices,
      monthly_user_price: plan.monthly_user_price,
      annually_user_price: plan.annually_user_price,
      free_workers_count: plan.free_workers_count
    };
  }
}
